import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CalendarDays, MapPin, Trophy } from 'lucide-react';
import { ProTournamentInfo } from '../../services/proTournamentInfoService';
import api from '../../services/api';

const ACCENT = '#3fced1';

export const buildFileUrl = (path?: string | null): string | null => {
  if (typeof path !== 'string') return null;
  const trimmed = path.trim();
  if (!trimmed) return null; // leer/whitespace = kein Bild
  if (trimmed.startsWith('data:') || trimmed.startsWith('http')) return trimmed;
  return `${api.defaults.baseURL}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`;
};

/**
 * Sortierung: Das als Nächstes anstehende Turnier zuerst.
 * LIVE (läuft jetzt) → UPCOMING nach Startdatum aufsteigend →
 * ohne Datum → COMPLETED (neueste zuerst) ans Ende.
 */
/**
 * Sortier-Datum eines Turniers: normalerweise startDate. Bei bereits
 * GESTARTETEN Dachturnieren (PDC Pro Tour: startDate 9. Feb) sortiert sonst
 * das alte Startdatum sie VOR nähere Events — dann zählt stattdessen die
 * NÄCHSTE Schedule-Session (≥ heute, Tagesanfang).
 */
export const timelineSortDate = (
  t: ProTournamentInfo,
  now: number = Date.now()
): number | null => {
  const start = typeof t.startDate === 'number' ? t.startDate : null;
  if (start == null || start > now) return start;
  const day = new Date(now);
  const todayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  const upcoming = (t.schedule ?? [])
    .map((e) => (typeof e.date === 'number' ? e.date : null))
    .filter((d): d is number => d !== null && d >= todayStart);
  return upcoming.length > 0 ? Math.min(...upcoming) : start;
};

export const sortByTimeline = (tournaments: ProTournamentInfo[]): ProTournamentInfo[] => {
  const now = Date.now();
  const rank = (t: ProTournamentInfo): number => {
    if (t.status === 'LIVE') return 0;
    if (t.status === 'UPCOMING') return 1;
    if (t.status === 'COMPLETED') return 3;
    return 2;
  };
  return [...tournaments].sort((a, b) => {
    const rankDiff = rank(a) - rank(b);
    if (rankDiff !== 0) return rankDiff;
    if (a.status === 'COMPLETED') {
      const aStart = typeof a.startDate === 'number' ? a.startDate : null;
      const bStart = typeof b.startDate === 'number' ? b.startDate : null;
      return (bStart ?? 0) - (aStart ?? 0); // Beendete: neueste zuerst
    }
    const aDate = timelineSortDate(a, now);
    const bDate = timelineSortDate(b, now);
    if (aDate == null && bDate == null) return 0;
    if (aDate == null) return 1;
    if (bDate == null) return -1;
    return aDate - bDate; // Kommende: nächstes zuerst
  });
};

/** Status-Chip (eckig) für den Content-Bereich der Karte — nie auf dem Bild. */
export const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  if (status === 'LIVE') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded bg-red-500/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
        Live
      </span>
    );
  }
  if (status === 'COMPLETED') {
    return (
      <span className="inline-flex items-center rounded bg-zinc-800 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-300">
        Beendet
      </span>
    );
  }
  return null;
};

export const TournamentCard: React.FC<{
  tournament: ProTournamentInfo;
  onClick: () => void;
  /** true = volle Breite (Grid auf /pdc-turniere), false = feste Slider-Breite */
  fluid?: boolean;
  /** Gesetzt → Karte ist ein echter Link (<a>), z.B. für interne SEO-Links (2026-09-03) */
  href?: string;
}> = ({ tournament, onClick, fluid = false, href }) => {
  const [imageError, setImageError] = useState(false);
  const thumbnail = buildFileUrl(tournament.thumbnail || tournament.heroImage);
  const showImage = thumbnail && !imageError;
  const orgTier = [tournament.organization, tournament.tier].filter(Boolean).join(' · ');

  const cardClassName = `group cursor-pointer overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 transition-all duration-300 hover:-translate-y-1 hover:border-[#3fced1]/50 hover:shadow-[0_16px_40px_-12px_rgba(63,206,209,0.3)] ${
    fluid ? 'w-full' : 'w-[300px] shrink-0 snap-start sm:w-[340px]'
  }`;
  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    href ? (
      <RouterLink
        to={href}
        aria-label={tournament.name || 'PDC-Turnier'}
        onClick={onClick}
        className={`block ${cardClassName}`}
      >
        {children}
      </RouterLink>
    ) : (
      <div
        role="button"
        tabIndex={0}
        aria-label={tournament.name || 'PDC-Turnier'}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        className={cardClassName}
      >
        {children}
      </div>
    );

  return (
    <Wrapper>
      {/* Banner (16:9 – KI-generiert) */}
      <div className="relative aspect-video w-full overflow-hidden bg-zinc-800">
        {showImage ? (
          <img
            src={thumbnail as string}
            alt={tournament.name || ''}
            loading="lazy"
            onError={() => setImageError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="relative flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 via-zinc-900 to-black">
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: 'radial-gradient(60% 80% at 30% 20%, rgba(63,206,209,0.12), transparent 65%)' }}
            />
            <Trophy size={36} className="text-zinc-600" />
          </div>
        )}
      </div>

      {/* Info: Status-Chip + Org/Tier über dem Titel */}
      <div className="p-4">
        {(tournament.status === 'LIVE' || tournament.status === 'COMPLETED' || orgTier) && (
          <div className="mb-2 flex items-center gap-1.5">
            <StatusBadge status={tournament.status} />
            {orgTier && (
              <span className="truncate text-[10.5px] text-zinc-500">{orgTier}</span>
            )}
          </div>
        )}
        <h3 className="line-clamp-1 text-base font-bold text-white transition-colors group-hover:text-[#3fced1]">
          {tournament.shortName || tournament.name}
        </h3>
        <div className="mt-2 space-y-1.5 text-sm text-gray-400">
          <div className="flex items-center gap-1.5">
            <CalendarDays size={14} className="shrink-0" style={{ color: ACCENT }} />
            <span className="line-clamp-1">{tournament.dateLabel || 'Termin folgt'}</span>
          </div>
          {(tournament.venue || tournament.city) && (
            <div className="flex items-center gap-1.5">
              <MapPin size={14} className="shrink-0" style={{ color: ACCENT }} />
              <span className="line-clamp-1">
                {[tournament.venue, tournament.city].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

/**
 * Startseiten-Slider: Professionelle Turniere in Timeline-Reihenfolge –
 * das als Nächstes anstehende Turnier steht vorne. Klick → /turniere/:slug.
 */

// Der frühere Default-Export (Startseiten-Slider) war ohne Import und wurde
// am 2026-09-15 entfernt. Aktiv sind nur die Named Exports oben
// (buildFileUrl, sortByTimeline, StatusBadge, TournamentCard).
